

































































































import {Component, Prop, Vue, Watch} from "vue-property-decorator";
import PortalInput from "@/components/common/PortalInput.vue";
import PortalTextarea from "@/components/common/PortalTextarea.vue";
import OwnershipSnapshotDTO from "@/dto/company/OwnershipSnapshotDTO";
import ShareholderDTO from "@/dto/company/ShareholderDTO";
import SaveOwnershipSnapshotPayload from "@/dto/company/SaveOwnershipSnapshotPayload";
import ItemPayload from "@/dto/company/ItemPayload";
import PortalSelect from "@/components/common/PortalSelect.vue";
import SelectOption from "@/components/common/SelectOption";
import CompanyService from "@/services/CompanyService";
import Workspaces from "@/state/Workspaces";
import Notifications from "@/state/Notifications";
import {processError} from "@/utils/ComponentUtils";

interface EditItem {
  shareholderId: number;
  name: string;
  share: number;
}

@Component({
  components: {PortalInput, PortalSelect, PortalTextarea}
})
export default class UpdateOwnershipModal extends Vue {
  @Prop()
  private snapshotToEdit!: OwnershipSnapshotDTO | null;

  @Prop()
  private latestSnapshot!: OwnershipSnapshotDTO | null;

  @Prop()
  private shareholders!: Array<ShareholderDTO>;

  @Prop()
  private onSaved!: () => void;

  private payload = new SaveOwnershipSnapshotPayload();
  private editItems: Array<EditItem> = [];
  private selectedShareholderId: number | null = null;
  private loading = false;
  private error = "";

  created() {
    if (this.snapshotToEdit) {
      this.payload.effectiveDate = this.snapshotToEdit.effectiveDate;
      this.payload.comment = this.snapshotToEdit.comment || "";
      this.editItems = this.snapshotToEdit.items
          .filter(item => !item.isUnassigned && item.shareholderId)
          .map(item => ({
            shareholderId: item.shareholderId!,
            name: item.shareholderName || 'Unknown',
            share: item.share
          }));
    } else {
      this.payload.effectiveDate = new Date().toISOString().split('T')[0];
      if (this.latestSnapshot) {
        this.editItems = this.latestSnapshot.items
            .filter(item => !item.isUnassigned && item.shareholderId)
            .map(item => ({
              shareholderId: item.shareholderId!,
              name: item.shareholderName || 'Unknown',
              share: item.share
            }));
      }
      this.payload.comment = "";
    }
  }

  get isEdit(): boolean {
    return !!this.snapshotToEdit;
  }

  get shareholderOptions(): Array<SelectOption> {
    return this.shareholders
        .filter(s => !this.editItems.find(item => item.shareholderId === s.id))
        .map(s => new SelectOption({
          title: s.person.name || 'Unknown',
          value: s.id
        }));
  }

  get totalShare(): number {
    return this.editItems.reduce((sum, item) => sum + (Number(item.share) || 0), 0);
  }

  get unallocatedShare(): number {
    return Math.max(0, 100 - this.totalShare);
  }

  get isValid(): boolean {
    // Check if total is 100 with small tolerance for floating point
    return Math.abs(this.totalShare - 100) < 0.0001 && !!this.payload.effectiveDate;
  }

  removeItem(index: number) {
    this.editItems.splice(index, 1);
  }

  @Watch('selectedShareholderId')
  onShareholderSelected(newId: number | null) {
    if (newId) {
      this.addShareholder();
    }
  }

  addShareholder() {
    if (!this.selectedShareholderId) return;

    const shareholder = this.shareholders.find(s => s.id === this.selectedShareholderId);
    if (shareholder) {
      this.editItems.push({
        shareholderId: shareholder.id,
        name: shareholder.person.name || 'Unknown',
        share: 0
      });
    }
    this.$nextTick(() => {
      this.selectedShareholderId = null;
    });
  }

  save() {
    if (!this.isValid) return;

    this.loading = true;
    this.error = "";
    
    this.payload.items = this.editItems.map(item => new ItemPayload(item.shareholderId, item.share));

    const action = this.isEdit
        ? CompanyService.updateOwnershipSnapshot(Workspaces.getCurrent.id, this.snapshotToEdit!.id!, this.payload)
        : CompanyService.saveOwnershipSnapshot(Workspaces.getCurrent.id, this.payload);

    action.then(
        () => {
          this.loading = false;
          Notifications.success(this.isEdit ? "Snapshot has been updated." : "Ownership structure has been updated.");
          this.onSaved();
          this.$modal.hideAll();
        },
        err => {
          this.loading = false;
          processError(err, this);
        }
    );
  }
}
